import React from 'react';
import { BarChart, Trash2, Layers } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных проектов трансформации
 */
const ComparisonSidebar = ({ 
  savedProjects, 
  deleteSavedProject, 
  loadSavedProject 
}) => {
  // Если нет сохраненных проектов
  if (!savedProjects || savedProjects.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение проектов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные варианты проектов трансформации для сравнения.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение проектов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedProjects.length} {pluralize(savedProjects.length, 'вариант', 'варианта', 'вариантов')}
        </span>
      </div>

      <div className="space-y-4">
        {savedProjects.map((project) => (
          <div 
            key={project.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {project.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Layers className="w-4 h-4" />}
                  onClick={() => loadSavedProject(project.id)}
                  title="Загрузить параметры"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedProject(project.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Тип:</span>
                <span className="font-medium">
                  {getTransformationTypeName(project.transformationType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Продолжительность:</span>
                <span>
                  {project.formData.projectDuration} мес.
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Размер команды:</span>
                <span>
                  {project.formData.internalTeamSize} чел.
                </span>
              </div>
              <div className="flex justify-between">
                <span>Стоимость:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(project.results.totalCost)}
                </span>
              </div>
              {project.results.roi && project.results.roi.roiPercent !== undefined && (
                <div className="flex justify-between mt-1">
                  <span>ROI (3 года):</span>
                  <span className={`font-medium ${project.results.roi.roiPercent >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatNumber(project.results.roi.roiPercent, 1)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {savedProjects.length >= 2 && (
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
          <h4 className="text-sm font-medium text-blue-700 dark:text-blue-300 mb-2">Сравнительный анализ</h4>
          <div className="text-sm text-blue-600 dark:text-blue-400">
            <div className="space-y-1">
              <p><strong>Средняя стоимость:</strong> {formatCurrency(getAverageCost(savedProjects))}</p>
              <p><strong>Разброс стоимости:</strong> {formatCurrency(getMinCost(savedProjects))} - {formatCurrency(getMaxCost(savedProjects))}</p>
              {hasROIData(savedProjects) && (
                <p><strong>Средний ROI:</strong> {formatNumber(getAverageROI(savedProjects), 1)}%</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

// Получение названия типа трансформации
const getTransformationTypeName = (typeId) => {
  const types = {
    comprehensive: 'Комплексная',
    digital: 'Цифровая',
    lean: 'Бережливое произв.',
    agile: 'Agile',
    automation: 'Автоматизация'
  };
  return types[typeId] || typeId;
};

// Получение средней стоимости проектов
const getAverageCost = (projects) => {
  if (!projects || projects.length === 0) return 0;
  const sum = projects.reduce((acc, project) => acc + (project.results?.totalCost || 0), 0);
  return sum / projects.length;
};

// Получение минимальной стоимости проектов
const getMinCost = (projects) => {
  if (!projects || projects.length === 0) return 0;
  return Math.min(...projects.map(project => project.results?.totalCost || 0));
};

// Получение максимальной стоимости проектов
const getMaxCost = (projects) => {
  if (!projects || projects.length === 0) return 0;
  return Math.max(...projects.map(project => project.results?.totalCost || 0));
};

// Проверка наличия данных ROI
const hasROIData = (projects) => {
  return projects.some(project => project.results?.roi?.roiPercent !== undefined);
};

// Получение среднего ROI проектов
const getAverageROI = (projects) => {
  if (!projects || projects.length === 0) return 0;
  const projectsWithROI = projects.filter(project => project.results?.roi?.roiPercent !== undefined);
  if (projectsWithROI.length === 0) return 0;
  
  const sum = projectsWithROI.reduce((acc, project) => acc + project.results.roi.roiPercent, 0);
  return sum / projectsWithROI.length;
};

export default ComparisonSidebar;